/*
  Opens The Bra Style Guide from the PDP's Size Guide drawer, on DESKTOP ONLY.

  Behaviour, per Alexander 2026-09-14:
    - Mobile: the guide opens as its own full-screen page, as it always did. The tap is
      intercepted only to show a loading state and to perform the navigation by hand —
      the size guide's modal cancels the link's default (see the handler below).
    - Desktop via the Size Guide: the guide CLOSES the size guide and opens in its place
      as the cart-style drawer — the same 520px right-hand panel the direct link gives,
      never the size guide's own 1013px rectangle (Alexander, 2026-09-15). One entry
      point, one shape. Closing the guide returns to the product page.
    - Desktop direct: /pages/faq?view=fit-quiz still works as a standalone page.

  Why fetched on demand: the quiz section is ~1.3MB, nearly all of it the products JSON
  for the whole bra range. It is pulled only when she asks for it, through Shopify's
  Section Rendering API (?sections=fit-quiz -> {"fit-quiz": "<html>"}).
*/
(function () {
  'use strict';

  // The PDP renders the size guide snippet twice (a collapsed copy and the live drawer),
  // so this file's <script> tag is emitted twice and the browser runs it twice. Without
  // this guard the click handler bound twice and one click built two stacked overlays.
  if (window.__fitQuizLaunchReady) return;
  window.__fitQuizLaunchReady = true;

  var DESKTOP = '(min-width: 990px)';

  // Asset URLs come from the script tag, so the stylesheet can start loading the instant
  // she clicks rather than after the 1.3MB section arrives. Without this the overlay and
  // its spinner were unstyled for the whole fetch — i.e. nothing visible at all while the
  // guide loaded, which read as a dead click.
  var TAG = document.currentScript ||
            document.querySelector('script[src*="fit-quiz-launch"]');
  var CSS_URL = TAG && TAG.getAttribute('data-fq-css');
  var JS_URL = TAG && TAG.getAttribute('data-fq-js');
  var HEART_URL = TAG && TAG.getAttribute('data-fq-heart');
  var cache = null;          // section HTML, so reopening in one session is instant
  var overlay = null;
  var scrim = null;          // the guide's own dimmer — the size guide took its own away
  var bodyOverflow = null;   // body's own inline overflow, put back on close
  var openToken = 0;         // identifies the current open, so a stale one can bail
  var openedAt = 0;          // guards against the opening click closing it again

  function isDesktop() {
    return !!(window.matchMedia && window.matchMedia(DESKTOP).matches);
  }

  // The PDP renders the size guide twice (a collapsed copy and the live drawer), so
  // always resolve from the link that was actually clicked, never by document lookup.
  function dialogFor(link) {
    return link.closest('.modal__dialog') ||
           link.closest('[role="dialog"]') ||
           link.closest('.modal__inner-content');
  }

  // The guide is always the same object — the cart-style drawer, sized to its own content
  // rather than to whatever opened it. Written inline because it has to hold before
  // fit-quiz.css exists; the stylesheet repeats it for the rest of the session.
  function place(el) {
    el.style.top = '0';
    el.style.right = '0';
    el.style.left = 'auto';
    el.style.width = 'min(520px, 100%)';
    el.style.height = '100%';
  }

  function close() {
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    if (scrim && scrim.parentNode) scrim.parentNode.removeChild(scrim);
    overlay = null;
    scrim = null;
    if (bodyOverflow !== null) {
      document.body.style.overflow = bodyOverflow;
      bodyOverflow = null;
    }
  }

  // Dismisses the size guide as the guide takes its place. Uses the theme's own close
  // button so the drawer tears down exactly as it normally would — focus returned, body
  // scroll-lock released, its scrim removed.
  function closeDrawer(dialog) {
    if (!dialog) return;
    var btn = dialog.querySelector('.modal__close-button');
    if (btn) { btn.click(); return; }
    var popup = dialog.closest ? dialog.closest('modal-popup') : null;
    if (popup && popup.removeAttribute) popup.removeAttribute('open');
  }

  // <script src> injected via innerHTML never executes, so the quiz's stylesheet and
  // script are appended to <head> by hand, once.
  function ensureAsset(tag, url) {
    if (!url) return Promise.resolve();
    var attr = tag === 'link' ? 'href' : 'src';
    if (document.head.querySelector(tag + '[' + attr + '="' + url + '"]')) return Promise.resolve();
    return new Promise(function (resolve) {
      var el = document.createElement(tag);
      if (tag === 'link') el.rel = 'stylesheet';
      el.onload = resolve;
      el.onerror = resolve;                 // a missing asset must not hang the drawer
      el[attr] = url;
      document.head.appendChild(el);
    });
  }

  function fetchSection(href) {
    if (cache) return Promise.resolve(cache);
    var url = href + (href.indexOf('?') > -1 ? '&' : '?') + 'sections=fit-quiz';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('section ' + r.status); return r.json(); })
      .then(function (j) {
        if (!j['fit-quiz']) throw new Error('no fit-quiz section in response');
        cache = j['fit-quiz'];
        return cache;
      });
  }

  function open(link, dialog) {
    // Read the href while the link is still in the document: closing the size guide can
    // take the drawer's markup — and this link with it — out of the page.
    var href = link.getAttribute('href');

    // The size guide gets out of the way rather than being covered up. It also owned the
    // scrim and the body scroll-lock, so the guide has to supply both from here on.
    closeDrawer(dialog);
    scrim = document.createElement('div');
    scrim.className = 'fq-overlay__scrim';
    // display:block is load-bearing, not tidiness. The scrim is an empty <div>, and the
    // theme's reset hides those outright (div:empty:not(.progress-bar__filling):not(
    // .cart-drawer__overlay) { display: none } in base.css) — which beats the stylesheet
    // rule on specificity, so the page behind simply never dimmed. Inline always wins.
    scrim.style.cssText = 'position:fixed;inset:0;z-index:9999;display:block;' +
      'background:rgba(0,0,0,0.5);';
    document.body.appendChild(scrim);
    // An inline style, NOT the theme's .overflow-hidden class: the modal's close handler is
    // debounced, so it runs a task later and ends with body.classList.remove('overflow-hidden')
    // — which would strip the lock we had just added and let the product page scroll behind
    // the open guide. The inline value is ours alone and survives that.
    if (bodyOverflow === null) bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    overlay = document.createElement('div');
    overlay.className = 'fq-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'The Bra Style Guide');
    // Inline, not from the stylesheet: these must hold before fit-quiz.css exists.
    overlay.style.cssText = 'position:fixed;z-index:10000;background:#f5f5f5;' +
      'border-left:1px solid #000;overflow-y:auto;display:flex;flex-direction:column;' +
      'align-items:center;justify-content:center;';
    var DOT = 'width:10px;height:10px;border:1px solid #000;border-radius:50px;' +
               'background:#87ffa2;animation:fq-load 1.05s ease-in-out';
    overlay.innerHTML =
      '<div class="fq__launching" role="status" aria-live="polite" ' +
      'style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;">' +
        (HEART_URL
          ? '<span class="fq__loadheart" aria-hidden="true" style="width:120px;max-width:40vw;' +
            'aspect-ratio:338/314;background-image:url(' + HEART_URL + ');background-size:600% 100%;' +
            'background-repeat:no-repeat;animation:fq-heart 3.84s step-end infinite;"></span>'
          : '') +
        '<span class="fq__loaddots" aria-hidden="true" style="display:flex;gap:8px;">' +
          '<span style="' + DOT + ' infinite;"></span>' +
          '<span style="' + DOT + ' .14s infinite;"></span>' +
          '<span style="' + DOT + ' .28s infinite;"></span>' +
        '</span>' +
      '</div>';
    place(overlay);
    // the keyframes live in fit-quiz.css, which is not here yet
    if (!document.getElementById('fq-load-kf')) {
      var kf = document.createElement('style');
      kf.id = 'fq-load-kf';
      kf.textContent =
        '@keyframes fq-load{0%,70%,100%{transform:translateY(0);opacity:.55}' +
        '35%{transform:translateY(-6px);opacity:1}}' +
        '@keyframes fq-heart{0%{background-position:0% 0}29.17%{background-position:20% 0}' +
        '31.25%{background-position:40% 0}33.33%{background-position:60% 0}' +
        '35.42%{background-position:80% 0}37.5%{background-position:40% 0}' +
        '41.67%{background-position:80% 0}72.92%{background-position:100% 0}' +
        '75%{background-position:20% 0}77.08%{background-position:0% 0}' +
        '100%{background-position:0% 0}}';
      document.head.appendChild(kf);
    }
    // start the stylesheet now, in parallel with the section fetch
    ensureAsset('link', CSS_URL);
    document.body.appendChild(overlay);
    openedAt = Date.now();
    // The overlay hangs off <body>, so the theme's modal treats clicks inside it as
    // "outside the dialog" and calls preventDefault on them — which silently cancelled
    // the consent checkbox's toggle. Stop events at the overlay boundary, the way any
    // modal layer should. Our own outside-click handler listens in the capture phase,
    // so it still sees everything it needs.
    ['click', 'mousedown', 'mouseup'].forEach(function (type) {
      overlay.addEventListener(type, function (e) { e.stopPropagation(); });
    });
    // Everything below belongs to THIS open. If she closes while the section is still in
    // flight, the overlay is gone and there is nothing to fill: bail quietly. Without this
    // the .then threw on a null overlay, the .catch read that as "the fetch failed", and
    // pressing Escape 250ms after opening navigated her to the standalone page — a ~2.9MB
    // load, triggered by the gesture people use precisely when the connection is slow.
    var token = ++openToken;
    var aborted = function () { return token !== openToken || !overlay; };

    fetchSection(href).then(function (html) {
      if (aborted()) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');

      var cssEl = doc.querySelector('link[rel="stylesheet"][href*="fit-quiz"]');
      var jsEl = doc.querySelector('script[src*="fit-quiz.js"]');
      var quiz = doc.querySelector('fit-quiz');
      if (!quiz) throw new Error('no <fit-quiz> in section');

      // The products JSON is a SIBLING of <fit-quiz> in the section, not a child, and the
      // element reads itself first and the document second. Move it inside, so this copy
      // can never read another copy's data.
      var data = doc.querySelector('[data-fq-products]');
      if (!data) throw new Error('no product data in section');
      quiz.insertBefore(data, quiz.firstChild);

      // The page-only scrim and fixed-drawer rules key off html.fq-page, which only the
      // standalone page sets (via a script that cannot run in injected markup), so nothing
      // here has to opt out of them.
      quiz.classList.add('fq--embedded');

      // Title and a way out, per the design. It used to say "‹ Back to size guide", which
      // was true while the guide sat on top of the still-open drawer; now the size guide is
      // closed before this opens, so there is nothing behind to go back to.
      var bar = document.createElement('div');
      bar.className = 'fq-overlay__bar';
      var heading = document.createElement('span');
      heading.className = 'fq-overlay__title';
      heading.textContent = 'Bra Style Guide';
      var exit = document.createElement('button');
      exit.type = 'button';
      exit.className = 'fq-overlay__x';
      exit.setAttribute('aria-label', 'Close the guide');
      exit.innerHTML = '<svg viewBox="0 0 14 14" aria-hidden="true">' +
        '<path d="M1.5 1.5l11 11M12.5 1.5l-11 11" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>';
      exit.addEventListener('click', close);
      bar.appendChild(heading);
      bar.appendChild(exit);

      if (aborted()) return;
      overlay.innerHTML = '';
      var inline = doc.querySelector('style');
      if (inline) overlay.appendChild(inline.cloneNode(true));
      overlay.appendChild(bar);
      overlay.appendChild(quiz);

      // the overlay is styled by the stylesheet from here on
      overlay.style.alignItems = '';
      overlay.style.justifyContent = '';
      return ensureAsset('link', CSS_URL || (cssEl && cssEl.getAttribute('href')))
        .then(function () {
          return ensureAsset('script', JS_URL || (jsEl && jsEl.getAttribute('src')));
        });
    }).catch(function (err) {
      // Only a genuine failure falls back to the page. If she closed the guide, or opened
      // it again, this open is simply stale — say nothing and go nowhere.
      if (aborted()) return;
      if (window.console && console.warn) console.warn('[fit-quiz] section load failed', err);
      close();
      window.location.href = href;
    });
  }

  // Any other page can open the same drawer with a plain link carrying data-fq-open (the
  // Bra Guide page's quiz banner, 2026-11-03): no size guide to close, and it opens in place
  // on every width — on a phone the drawer is simply full width — so the page never reloads
  // or scrolls. The link's href is the fallback if the script never loads.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-fq-open]');
    if (!link || overlay) return;
    e.preventDefault();
    open(link, null);
  });

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('.bra-calc__teaser-btn');
    if (!link) return;
    if (!isDesktop()) {
      // Mobile navigates to the standalone page, which is a ~2.9MB document — several
      // seconds on a phone connection with no feedback, so the tap felt dead. Put the
      // button into a loading state first.
      if (!link.classList.contains('is-loading')) {
        link.classList.add('is-loading');
        var label = link.querySelector('span');
        if (label) label.textContent = 'Loading the guide\u2026';
      }
      // Go there ourselves rather than letting the browser follow the href. While the size
      // guide is open its modal listens on <body> and calls preventDefault() on every click
      // whose target is not LITERALLY an <a> (handleBodyClick, features/modal/scripts). The
      // tap lands on the <span> inside this link, so that test misses and the navigation was
      // cancelled - the button sat on "Loading the guide..." for ever. Measured on live DK:
      // the same event reads defaultPrevented false at document capture and true by the time
      // it reaches window.
      e.preventDefault();
      window.location.href = link.getAttribute('href');
      return;
    }
    var dialog = dialogFor(link);
    if (!dialog) return;                 // not inside the drawer: let it navigate
    e.preventDefault();
    open(link, dialog);
  });

  // A click on the dimmed page outside the guide closes it, leaving her on the product
  // page. Hit-tested by COORDINATES, not by element: every click inside the quiz
  // re-renders the view and detaches the clicked node before this listener runs, so
  // contains(target) reports false for the quiz's own buttons.
  document.addEventListener('click', function (e) {
    if (!overlay) return;
    if (Date.now() - openedAt < 400) return;       // the click that opened it
    if (e.detail === 0) return;                    // keyboard activation
    var r = overlay.getBoundingClientRect();
    if (e.clientX >= r.left && e.clientX <= r.right &&
        e.clientY >= r.top && e.clientY <= r.bottom) return;
    close();
  }, true);

  // Escape closes the guide and returns to the product page.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay) { e.stopPropagation(); close(); }
  });
})();
